import type { ReactNode } from 'react';
import type { DataTableColumn } from '../types';
import { buildColumn, EMPTY_VALUE, type ColumnPresetOptions } from './base';

const DEFAULT_DATE_FORMAT: Intl.DateTimeFormatOptions = {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
};

/** `Date`, epoch millis or a parseable string in, a valid `Date` or null out. */
function toDate(value: string | number | Date | null | undefined): Date | null {
    if (value === null || value === undefined || value === '') {
        return null;
    }
    const date = value instanceof Date ? value : new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
}

export type DateColumnOptions<T, K extends string = string> = ColumnPresetOptions<T, K> & {
    /** Anything `Date`-able. Null, empty and unparseable values render `fallback`. */
    value: (row: T) => string | number | Date | null | undefined;
    /** `Intl.DateTimeFormat` options. Defaults to e.g. "Aug 6, 2026". */
    format?: Intl.DateTimeFormatOptions;
    /** BCP 47 tag. Omitted, the runtime's locale is used. */
    locale?: string;
    /** Full control, bypassing `Intl` entirely. Wins over `format`/`locale`. */
    formatter?: (date: Date, row: T) => string;
    fallback?: ReactNode;
    className?: string;
};

/**
 * A formatted date. Null-safe by contract: a missing or unparseable value
 * renders `fallback` (an em dash) rather than "Invalid Date".
 *
 * The `Intl.DateTimeFormat` is constructed once per column, not per cell.
 */
export function dateColumn<T, K extends string = string>(
    options: DateColumnOptions<T, K>
): DataTableColumn<T, K> {
    const { value, format, locale, formatter, fallback = EMPTY_VALUE, className } = options;
    const intl = formatter ? null : new Intl.DateTimeFormat(locale, format ?? DEFAULT_DATE_FORMAT);
    const textFor = (row: T): string => {
        const date = toDate(value(row));
        if (!date) {
            return '';
        }
        return formatter ? formatter(date, row) : (intl as Intl.DateTimeFormat).format(date);
    };

    return buildColumn(
        options,
        (row) => {
            const text = textFor(row);
            if (!text) {
                return fallback;
            }
            return className ? <span className={className}>{text}</span> : text;
        },
        { truncate: true, getTitle: (row) => textFor(row) || undefined }
    );
}

export type AmountColumnOptions<T, K extends string = string> = ColumnPresetOptions<T, K> & {
    /** The number. `null`/`undefined` renders `fallback`; `0` renders "0". */
    value: (row: T) => number | null | undefined;
    /** ISO 4217 code, e.g. `'USD'`. Set it to format as currency. */
    currency?: string;
    /** `Intl.NumberFormat` options, merged over the currency defaults. */
    format?: Intl.NumberFormatOptions;
    locale?: string;
    /** Full control, bypassing `Intl`. Wins over `currency`/`format`/`locale`. */
    formatter?: (value: number, row: T) => string;
    fallback?: ReactNode;
    className?: string;
};

/**
 * A currency or plain number, **right-aligned by default** (digits read as a
 * column only when their least-significant place lines up) and null-safe.
 *
 * The `Intl.NumberFormat` is constructed once per column, not per cell.
 */
export function amountColumn<T, K extends string = string>(
    options: AmountColumnOptions<T, K>
): DataTableColumn<T, K> {
    const {
        value,
        currency,
        format,
        locale,
        formatter,
        fallback = EMPTY_VALUE,
        className
    } = options;
    const intl = formatter
        ? null
        : new Intl.NumberFormat(locale, {
              ...(currency ? { style: 'currency' as const, currency } : {}),
              ...format
          });
    const textFor = (row: T): string => {
        const amount = value(row);
        if (amount === null || amount === undefined || Number.isNaN(amount)) {
            return '';
        }
        return formatter ? formatter(amount, row) : (intl as Intl.NumberFormat).format(amount);
    };

    return buildColumn(
        options,
        (row) => {
            const text = textFor(row);
            if (!text) {
                return fallback;
            }
            return className ? <span className={className}>{text}</span> : text;
        },
        { align: 'right', truncate: true, getTitle: (row) => textFor(row) || undefined }
    );
}
